/**
 * The create-entity form, read off the case schema.
 *
 * Pure: which types the dialog offers, which fields a type gets and in what
 * order, what blocks the Create button, and the request body. The server holds
 * the same rules (services/manual_graph/rules.py) and stays the source of truth;
 * this is the instant feedback.
 */

import { isMinimalNode } from '@/components/neo4j/cytoscape/entity-icons'
import { GEO_NUMERIC_TYPES } from '@/features/schema/format'
import type { ManualPropertyValue } from '@/services/api/manual-graph'
import type { EntityType, PropertySchema, Schema } from '@/services/api/schemas'

export type FieldKind = 'text' | 'integer' | 'number' | 'boolean' | 'date' | 'datetime'

export interface EntityField {
  name: string
  kind: FieldKind
  description?: string
  required: boolean
  unique: boolean
  isDisplayName: boolean
  /** Shown up front: the name, required and unique fields. The rest fold away. */
  primary: boolean
}

export type FormValues = Record<string, string | boolean | undefined>

export type FieldError = 'required' | 'invalid_integer' | 'invalid_number'

const KIND_BY_TYPE: Record<string, FieldKind> = {
  INTEGER: 'integer',
  INT: 'integer',
  LONG: 'integer',
  BOOLEAN: 'boolean',
  DATE: 'date',
  ZONED_DATETIME: 'datetime',
  DATETIME: 'datetime',
}

function kindOf(declaredType: string | undefined): FieldKind {
  const type = (declaredType || 'STRING').toUpperCase()
  return GEO_NUMERIC_TYPES.has(type) ? 'number' : (KIND_BY_TYPE[type] ?? 'text')
}

/** The schema's entity types an analyst may create (not the pipeline's own), alphabetically. */
export function creatableEntityTypes(schema: Schema | null | undefined): EntityType[] {
  return (schema?.entity_types ?? [])
    .filter(t => t.label && !isMinimalNode(t.label))
    .sort((a, b) => a.label.localeCompare(b.label))
}

/** The property that names an entity: the one marked display name, when exactly one is. */
export function displayProperty(entityType: EntityType): PropertySchema | undefined {
  const marked = entityType.properties.filter(p => p.is_display_name)
  return marked.length === 1 ? marked[0] : undefined
}

function fieldOf(prop: PropertySchema, isDisplayName: boolean): EntityField {
  const required = Boolean(prop.required) || isDisplayName
  const unique = Boolean(prop.unique)
  return {
    name: prop.name,
    kind: kindOf(prop.type),
    description: prop.description?.trim() || undefined,
    required,
    unique,
    isDisplayName,
    primary: isDisplayName || required || unique,
  }
}

/**
 * The type's fields: its display name first, then required, then unique, then
 * the rest — declaration order within each group. The display-name field is
 * required here even when the schema does not say so: an entity nobody can
 * name is a dot on the graph nobody can find.
 */
export function entityFields(entityType: EntityType): EntityField[] {
  const display = displayProperty(entityType)
  const rank = (f: EntityField) => (f.isDisplayName ? 0 : f.required ? 1 : f.unique ? 2 : 3)
  return entityType.properties
    .filter(p => p.name?.trim())
    .map(p => fieldOf(p, p.name === display?.name))
    .map((field, index) => ({ field, index }))
    .sort((a, b) => rank(a.field) - rank(b.field) || a.index - b.index)
    .map(({ field }) => field)
}

function isEmpty(value: string | boolean | undefined): boolean {
  return value === undefined || (typeof value === 'string' && value.trim() === '')
}

function numberError(kind: FieldKind, raw: string): FieldError | null {
  const parsed = Number(raw.trim().replace(',', '.'))
  if (!Number.isFinite(parsed)) return kind === 'integer' ? 'invalid_integer' : 'invalid_number'
  if (kind === 'integer' && !Number.isInteger(parsed)) return 'invalid_integer'
  return null
}

/** What blocks Create, by field. Empty = the form may be sent. */
export function validateEntityForm(
  fields: EntityField[],
  values: FormValues
): Record<string, FieldError> {
  const errors: Record<string, FieldError> = {}
  for (const field of fields) {
    const value = values[field.name]
    if (field.kind === 'boolean') continue
    if (isEmpty(value)) {
      if (field.required) errors[field.name] = 'required'
      continue
    }
    if (field.kind === 'integer' || field.kind === 'number') {
      const error = numberError(field.kind, String(value))
      if (error) errors[field.name] = error
    }
  }
  return errors
}

/** The request body's properties: typed values, empty fields left out. */
export function toRequestProperties(
  fields: EntityField[],
  values: FormValues
): Record<string, ManualPropertyValue> {
  const properties: Record<string, ManualPropertyValue> = {}
  for (const field of fields) {
    const value = values[field.name]
    if (field.kind === 'boolean') {
      if (typeof value === 'boolean') properties[field.name] = value
      continue
    }
    if (isEmpty(value)) continue
    const text = String(value).trim()
    properties[field.name] =
      field.kind === 'integer' || field.kind === 'number' ? Number(text.replace(',', '.')) : text
  }
  return properties
}

/** Whether the form holds anything a duplicate could be found by: the name or a unique value. */
export function hasLookupValue(fields: EntityField[], values: FormValues): boolean {
  return fields.some(
    f => (f.isDisplayName || f.unique) && f.kind !== 'boolean' && !isEmpty(values[f.name])
  )
}

/** A type's human name: the schema's override for this language, else the label split into words. */
export function entityTypeName(entityType: EntityType, language?: string): string {
  const override = language ? entityType.display_label?.[language] : undefined
  return override?.trim() || humanizeIdentifier(entityType.label)
}

/** `org_no` → `Org no`, `firstName` → `First name`, `WORKS_FOR` → `Works for`, `IBAN` stays `IBAN`. */
export function humanizeIdentifier(identifier: string): string {
  if (/^[A-Z0-9]+$/.test(identifier)) return identifier
  if (/^[A-Z0-9_]+$/.test(identifier)) return humanizeIdentifier(identifier.toLowerCase())
  const words = identifier
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .split(/[_\s]+/)
    .filter(Boolean)
  const sentence = words.map(w => (w === w.toUpperCase() ? w : w.toLowerCase())).join(' ')
  return sentence.charAt(0).toUpperCase() + sentence.slice(1)
}
